import { useTranslation } from "react-i18next";
import { Payment } from "@mui/icons-material";
import { useSelector } from "react-redux";
import PaymentStatus from "../InvoicesTableComponents/PaymentStatus/PaymentStatus";
import PayButton from "../../PaymentPlatform/PayButton";
import { DownloadButton } from "../components/DownloadButton";
import useLangs from "@/languages/hooks/useLangs";
import { useEffect, useState } from 'react';
import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";
import { Grid } from "@mui/material";
import dayjs from "dayjs";
import CrmButton from "../components/CrmButton";
import FeatureFlag from "@/components/FeatureFlag/FeatureFlag";


const useInvoicesTable = ({type}) => {
  const Settings = useSettingsContext();
  const { t } = useTranslation();
  const loaded = useSelector((state) => state.invoices[type].loaded);
  const invoices = useSelector((state) => state.invoices[type].items);
  const [options, setOptions] = useState({'pdf': {name: t('common:text.invoices_view_download_button'), type: 'pdf'}});

  const { currentLang } = useLangs();

  const paymentProvider = Settings?.paymentProvider ?? "stripe";

  useEffect(() => {
    if (!Settings?.invoices?.downloadTypes)
      return
    let optionsCopy = {...options}
    for ( let setting of Settings?.invoices?.downloadTypes || []) {
      if (!optionsCopy[setting]){
        let nameValue = ''
        let btnType = ''

        if (setting === 'signed_pdf') {
          nameValue = t('common:text.invoices_view_download_signed_button')
          btnType = 'signed_pdf'
        }
        else if (setting === 'curve' && type == 'electricity') {
          nameValue = t('common:text.invoices_view_download_curve')
          btnType = 'curve_csv'
        }
        else if (setting === 'signed_xml'){
          name_value = t('common:text.invoices_view_download_signed_xml_button')
          type = 'signed_xml'
        }

        if(name_value && btn_type){
          optionsCopy[setting] = {
            name: nameValue,
            type: btnType
          }
        }
      }
    }
    setOptions(optionsCopy);
  }, [Settings]);

  const invoiceShowTotalArray = Settings?.invoices?.showTotal ?? ['amount_total'];

  const columnsElectricity = [
    {
      title: t('common:text.invoices_invoice_number'),
      key: "num",
      computeValue: (invoice) => invoice.number
    }, {
      title: t('common:text.invoices_date'),
      key: "date",
      computeValue: (invoice) => dayjs(invoice.date).format("L")
    }, {
      title: t('common:text.invoices_period'),
      key: "period",
      computeValue: (invoice) => `${dayjs(invoice.start_date).format("L")} > ${dayjs(invoice.end_date).format("L")}`
    }, {
      title: t('common:text.invoices_address'),
      key: "address",
      computeValue: (invoice) => invoice.cups.full_address
    }, {
      title: t('common:text.invoices_energy'),
      key: "energy",
      computeValue: (invoice) => `${invoice.energy_consumed.toLocaleString(currentLang)} kWh`
    },
    invoiceShowTotalArray.includes('amount_total') && {
      title: t('common:text.invoices_import'),
      key: "import",
      computeValue: (invoice) => `${invoice.amount_total.toLocaleString(currentLang)} €`
    },
    invoiceShowTotalArray.includes('amount_to_pay') && {
      title: t('common:text.invoices_paying_import'),
      key: "import_to_pay",
      computeValue: (invoice) => invoice?.amount_to_pay && `${invoice.amount_to_pay.toLocaleString(currentLang)} €`
    }, {
      title: t('common:text.invoices_pending'),
      key: "pending",
      computeValue: (invoice) => <PaymentStatus invoice={invoice} />,
    }, {
      title: t('common:text.invoices_actions'),
      key: "actions",
      computeValue: (invoice) =>
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={1}
      >
        <Grid item>
          <DownloadButton
            invoiceId={invoice.id}
            type={type}
            options={options}
            historic={invoice.historic}
          />
        </Grid>
        {Settings.invoices?.showContactButton && Settings.features?.contact && (
          <FeatureFlag flagName="crm.case">
            <Grid item>
              <CrmButton />
            </Grid>
          </FeatureFlag>
        )}
        { 
          Settings.invoices?.enablePayment && 
          invoice?.online_payment?.payment_enabled &&
          (
            paymentProvider !== 'monei' || invoice?.online_payment?.payment_link
          ) && (
            <Grid item>
              <PayButton
                platformName={paymentProvider}
                payableDataId={invoice.invoice_id}
                relatedObjectType={invoice?.online_payment?.rel_object_type}
                paymentLink={invoice?.online_payment?.payment_link}
              >
                {<Payment />} {t('common:text.payment_form_pay', {amount: `${invoice.amount_pending.toLocaleString(currentLang)} €`,})}
              </PayButton>
            </Grid>
          )
        }
      </Grid>
    }
  ].filter(Boolean);

  const columnsMultipoint = [
    {
      title: t('common:text.invoices_invoice_number'),
      key: "num",
      computeValue: (invoice) => invoice.number
    }, {
      title: t('common:text.invoices_date'),
      key: "date",
      computeValue: (invoice) => dayjs(invoice.date).format("L")
    }, {
      title: t('common:text.invoices_address'),
      key: "address",
      computeValue: (invoice) => invoice.address_invoice_id	
    }, {
      title: t('common:text.invoices_import'),
      key: "import",
      computeValue: (invoice) => invoice.amount_total
    }, 
    // TODO: Implementar l'API per retornar aquestes dades
    // {
    //   title: t('common:text.invoices_pending'),
    //   key: "pending",
    //   computeValue: (invoice) => !invoice.paid && `${invoice.amount_pending.toLocaleString(currentLang)} €`
    // },
    {
      title: t('common:text.invoices_actions'),
      key: "actions",
      computeValue: (invoice) => <DownloadButton 
          invoiceId={invoice.id} 
          type={type}
          options={options}
          historic={invoice.historic}
        />
    }
  ]

  const columns = type === 'multipoint' ? columnsMultipoint : columnsElectricity;

  return {
    invoices, columns, loaded
  }
}

export default useInvoicesTable;
